import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { ink, surface, line, radius, shadow, font, bp, transition } from 'features/dashboard/tokens'
import WarningAmberIcon       from '@mui/icons-material/WarningAmber'
import LaunchRoundedIcon      from '@mui/icons-material/LaunchRounded'
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import { criticalityLabel, criticalityTint } from 'features/compliance/obligationConstants'

const MissingObligations = ({
  items = [],
  onOpenBp,
  onTrackOnBp,
  trackingSlug,
  onMarkNotApplicable,
}) => {
  const [naTarget, setNaTarget] = useState(null)
  const [naReason, setNaReason] = useState('')
  const [pickerTarget, setPickerTarget] = useState(null)

  if (!items.length) return null

  const confirmNa = () => {
    if (!naTarget) return
    onMarkNotApplicable?.(naTarget.bpId, naTarget.librarySlug, naReason.trim() || null)
    setNaTarget(null)
    setNaReason('')
  }

  const openTrackPicker = (item, intent) => setPickerTarget({ item, intent })
  const handlePick = bp => {
    if (!pickerTarget) return
    if (pickerTarget.intent === 'track') {
      onTrackOnBp?.(bp.id, pickerTarget.item.slug)
    } else {
      setNaTarget({
        bpId: bp.id,
        bpName: bp.name,
        librarySlug: pickerTarget.item.slug,
        obligationName: pickerTarget.item.name,
      })
    }
    setPickerTarget(null)
  }

  return (
    <Section>
      <Header>
        <Title>
          <TitleIconWrap>
            <WarningAmberIcon style={{ color: '#b00020', fontSize: 22 }} />
          </TitleIconWrap>
          <span>Suggested obligations not yet tracked</span>
        </Title>
        <Hint>Curated against UK Building Safety regulation</Hint>
      </Header>

      <List>
        {items.map((item, idx) => {
          const samples = item.bpExamples || []
          const remaining = Math.max(0, item.bpCount - samples.length)
          // First example's reason is representative — the gap detector
          // returns the same explanation for every BP under one obligation,
          // so we only show it once at the obligation level.
          const reason = samples[0]?.reason
          const isTracking = trackingSlug === item.slug
          const KindIcon = item.obligationKind === 'file_type' ? DescriptionRoundedIcon : EventRepeatRoundedIcon
          const kindLabel = item.obligationKind === 'file_type' ? 'Document' : 'Recurring task'
          const kindAccent = item.obligationKind === 'file_type' ? '#33becc' : '#D24724'

          return (
            <Row key={item.slug} $stagger={idx}>
              <Lead>
                <Name>
                  {item.name}
                  <Pill $colour={criticalityTint(item.criticalityWeight)}>
                    {criticalityLabel(item.criticalityWeight)}
                  </Pill>
                </Name>
                <Meta>
                  <Framework>{item.regulatoryFramework}</Framework>
                  {item.citationUrl && (
                    <CitationLink href={item.citationUrl} target='_blank' rel='noopener noreferrer'>
                      <LaunchRoundedIcon style={{ fontSize: 12 }} /> citation
                    </CitationLink>
                  )}
                  <KindTag $accent={kindAccent}>
                    <KindIcon style={{ fontSize: 14 }} /> {kindLabel}
                  </KindTag>
                </Meta>
                {item.description && <Description>{item.description}</Description>}
                {reason && <Reason>{reason}</Reason>}
              </Lead>

              <Right>
                <BpStrip>
                  <BpStripLabel>
                    Missing on <strong>{item.bpCount}</strong>{' '}
                    {item.bpCount === 1 ? 'Building Passport' : 'Building Passports'}
                    {samples.length > 0 && ':'}
                  </BpStripLabel>
                  {samples.map(bp => (
                    <BpChip key={bp.id} onClick={() => onOpenBp?.(bp.id)} title='Open compliance for this Building Passport'>
                      {bp.name}
                    </BpChip>
                  ))}
                  {remaining > 0 && <BpMore>+{remaining} more</BpMore>}
                </BpStrip>

                <Actions>
                  {onTrackOnBp && (
                    <PrimaryCta
                      disabled={isTracking}
                      onClick={() => openTrackPicker(item, 'track')}
                    >
                      Track on a Building Passport →
                    </PrimaryCta>
                  )}
                  {onMarkNotApplicable && (
                    <SecondaryCta onClick={() => openTrackPicker(item, 'na')}>
                      Mark not applicable
                    </SecondaryCta>
                  )}
                </Actions>
              </Right>
            </Row>
          )
        })}
      </List>

      {pickerTarget && (
        <ModalBackdrop onClick={() => setPickerTarget(null)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalTitle>
              {pickerTarget.intent === 'track'
                ? `Track ${pickerTarget.item.name} on…`
                : `Mark ${pickerTarget.item.name} as not applicable on…`}
            </ModalTitle>
            <ModalBody>Pick a Building Passport to apply this to.</ModalBody>
            <PickerList>
              {(pickerTarget.item.bpExamples || []).map(bp => (
                <PickerItem key={bp.id} onClick={() => handlePick(bp)}>
                  <PickerName>{bp.name}</PickerName>
                  <PickerHint>→</PickerHint>
                </PickerItem>
              ))}
              {pickerTarget.item.bpCount > (pickerTarget.item.bpExamples?.length || 0) && (
                <PickerNote>
                  Showing the {pickerTarget.item.bpExamples?.length || 0} highest-impact Building Passports. Open a Building Passport's compliance tab to manage the rest individually.
                </PickerNote>
              )}
            </PickerList>
            <ModalActions>
              <SecondaryAction onClick={() => setPickerTarget(null)}>Cancel</SecondaryAction>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}

      {naTarget && (
        <ModalBackdrop onClick={() => setNaTarget(null)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalTitle>Mark as not applicable</ModalTitle>
            <ModalBody>
              <strong>{naTarget.obligationName}</strong> will be hidden from the
              missing-obligations panel for <strong>{naTarget.bpName}</strong>.
              You can give a brief reason so future reviewers know why.
            </ModalBody>
            <ReasonInput
              autoFocus
              placeholder='e.g. "No gas supply on site" or "Sprinklered to BS 9251 instead"'
              value={naReason}
              onChange={e => setNaReason(e.target.value)}
              maxLength={1000}
            />
            <ModalActions>
              <SecondaryAction onClick={() => setNaTarget(null)}>Cancel</SecondaryAction>
              <PrimaryAction onClick={confirmNa}>Mark not applicable</PrimaryAction>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}
    </Section>
  )
}

export default MissingObligations

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Section = styled.section`
  margin-bottom: 32px;
  padding: 22px 24px 24px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #f0d8d0;
  border-left: 5px solid #b00020;
  border-radius: ${radius.md};
  box-shadow: ${shadow.card};
`

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`

const Title = styled.h3`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
  display: inline-flex;
  align-items: center;
  gap: 10px;
`

const TitleIconWrap = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  border-radius: ${radius.round};
  background: rgba(204, 34, 0, 0.10);
  border: 1px solid rgba(204, 34, 0, 0.18);
`

const Hint = styled.span`
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 18px;
  padding: 16px 18px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  align-items: stretch;
  transition: ${transition};
  animation: ${fadeUp} 420ms ease both;
  animation-delay: ${({ $stagger }) => 60 + $stagger * 40}ms;

  &:hover {
    transform: translateY(-1px);
    border-color: ${line.strong};
    box-shadow: ${shadow.lift};
  }

  @media (max-width: ${bp.tablet}) {
    grid-template-columns: 1fr;
  }
`

const Lead = styled.div`
  min-width: 0;
  display: flex; flex-direction: column; gap: 6px;
`

const Name = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  font-weight: 700;
  font-size: ${font.emphasis};
  color: ${({ theme }) => theme.colors.haiti};
  letter-spacing: -0.1px;
`

const Pill = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: ${radius.pill};
  background: ${({ $colour }) => `${$colour}1A`};
  color: ${({ $colour }) => $colour};
  border: 1px solid ${({ $colour }) => `${$colour}33`};
`

const Meta = styled.div`
  font-size: ${font.caption};
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-top: 2px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

const Framework = styled.span`
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.06em;
`

const CitationLink = styled.a`
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;
  display: inline-flex; align-items: center; gap: 4px;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.06em;
  &:hover { text-decoration: underline; }
`

const KindTag = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px;
  border-radius: ${radius.pill};
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${({ $accent }) => `${$accent}14`};
  color: ${({ $accent }) => $accent};
  border: 1px solid ${({ $accent }) => `${$accent}33`};
`

const Description = styled.p`
  margin: 4px 0 0;
  font-size: ${font.body};
  color: ${ink.soft};
  line-height: 1.5;
`

const Reason = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  font-size: ${font.small};
  font-style: italic;
  color: ${ink.soft};
  background: ${surface.wash};
  border-left: 3px solid ${line.strong};
  border-radius: 0 ${radius.sm} ${radius.sm} 0;
  line-height: 1.45;
`

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;
  align-items: stretch;
`

const BpStrip = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
  align-items: center;
`

const BpStripLabel = styled.span`
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.haiti};
  margin-right: 4px;
`

const BpChip = styled.button`
  font: inherit;
  font-size: ${font.small};
  font-weight: 600;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid ${line.base};
  color: ${({ theme }) => theme.colors.haiti};
  padding: 3px 10px;
  border-radius: ${radius.pill};
  cursor: pointer;
  transition: ${transition};
  &:hover {
    background: ${({ theme }) => theme.colors.haiti};
    color: #fff;
    border-color: ${({ theme }) => theme.colors.haiti};
  }
`

const BpMore = styled.span`
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
  padding: 3px 0 3px 4px;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const PrimaryCta = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  color: #fff;
  font-size: ${font.small};
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 7px 14px;
  border-radius: ${radius.md};
  cursor: pointer;
  transition: ${transition};
  &:hover:not(:disabled) { filter: brightness(1.06); transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: progress; }
`

const SecondaryCta = styled.button`
  font: inherit;
  background: transparent;
  border: 1px solid ${line.strong};
  color: ${ink.soft};
  font-size: ${font.small};
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 7px 14px;
  border-radius: ${radius.md};
  cursor: pointer;
  transition: ${transition};
  &:hover {
    background: ${surface.wash};
    border-color: ${line.strong};
    color: ${({ theme }) => theme.colors.haiti};
  }
`

const ModalBackdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 1200;
`

const ModalCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${radius.md};
  padding: 24px 28px;
  width: 90%; max-width: 520px;
  box-shadow: ${shadow.overlay};
`

const ModalTitle = styled.h4`
  margin: 0 0 12px;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  color: ${({ theme }) => theme.colors.haiti};
`

const ModalBody = styled.p`
  margin: 0 0 14px;
  font-size: ${font.body};
  color: ${ink.soft};
  line-height: 1.5;
`

const PickerList = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  margin-bottom: 8px;
`

const PickerItem = styled.button`
  font: inherit;
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  cursor: pointer;
  text-align: left;
  &:hover {
    background: ${({ theme }) => theme.colors.haiti};
    color: #fff;
    border-color: ${({ theme }) => theme.colors.haiti};
  }
`

const PickerName = styled.span`
  font-size: ${font.body}; font-weight: 600;
  color: inherit;
`

const PickerHint = styled.span`
  font-size: ${font.heading}; opacity: 0.6;
`

const PickerNote = styled.p`
  margin: 6px 0 0;
  font-size: ${font.small}; color: ${({ theme }) => theme.colors.grey[800]};
  font-style: italic;
`

const ReasonInput = styled.textarea`
  width: 100%; min-height: 76px; resize: vertical;
  border: 1px solid ${line.base}; border-radius: ${radius.sm};
  padding: 10px 12px;
  font-family: inherit; font-size: ${font.body};
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.secondary}; }
`

const ModalActions = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 16px;
`

const SecondaryAction = styled.button`
  font: inherit;
  background: transparent;
  border: 1px solid ${line.base};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.small}; font-weight: 600;
  padding: 8px 16px; border-radius: ${radius.sm};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const PrimaryAction = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${font.small}; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 8px 16px; border-radius: ${radius.sm};
  cursor: pointer;
  &:hover { filter: brightness(1.05); }
`
